import React, { useState } from 'react';
import { Paper, Typography, Box, useTheme, useMediaQuery, Tooltip } from '@mui/material';
import { styled } from '@mui/material/styles';
import { useAppStore } from '@/store';
import mainCards, { MainCardData } from '@/config/mainCards.tsx';
import researcherCards, { ResearcherCardData } from '@/config/researcherCards';

const CardPaper = styled(Paper)(({ theme }) => ({
  background: theme.palette.mode === 'light' 
    ? 'rgba(255, 255, 255, 0.9)' 
    : 'rgba(66, 66, 66, 0.9)',
  backdropFilter: 'blur(8px)',
  color: theme.palette.text.primary,
  borderRadius: '12px',
  padding: theme.spacing(0.75, 1.5),
  boxShadow: '0 1px 6px rgba(0, 24, 39, 0.06)',
  border: theme.palette.mode === 'light'
    ? '1px solid rgba(0, 24, 39, 0.08)'
    : '1px solid rgba(255, 255, 255, 0.1)',
  cursor: 'pointer',
  transition: 'all 0.2s ease-in-out',
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  gap: theme.spacing(1),
  minHeight: '36px',
  '&:hover': {
    background: theme.palette.mode === 'light'
      ? 'rgba(255, 255, 255, 1)'
      : 'rgba(77, 77, 77, 1)',
    boxShadow: '0 2px 12px rgba(0, 24, 39, 0.1)',
    transform: 'translateY(-1px)',
    borderColor: theme.palette.mode === 'light'
      ? 'rgba(0, 24, 39, 0.15)'
      : 'rgba(255, 255, 255, 0.2)',
  },
}));

const IconWrapper = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 20,
  height: 20,
  borderRadius: theme.shape.borderRadius,
  background: theme.palette.mode === 'light'
    ? 'rgba(0, 24, 39, 0.06)'
    : 'rgba(255, 255, 255, 0.08)',
  '& svg': {
    fontSize: '16px',
  },
}));

type CardData = MainCardData | ResearcherCardData;

interface MainCardsProps {
  cards?: CardData[];
}

const MainCards: React.FC<MainCardsProps> = ({ cards = mainCards }) => {
  const setMessage = useAppStore(s => s.setMessage);
  const theme = useTheme();
  const isSmallDevice = useMediaQuery(theme.breakpoints.down('sm'));
  const isMediumDevice = useMediaQuery(theme.breakpoints.between('sm', 'md'));
  const isCompactDevice = isSmallDevice || isMediumDevice;
  const [showAllSmallCards, setShowAllSmallCards] = useState(false);

  const cardDensitySx = isSmallDevice
    ? { borderRadius: '10px', px: 1, py: 0.4, minHeight: '30px', gap: 0.5 }
    : isMediumDevice
      ? { borderRadius: '11px', px: 1.25, py: 0.5, minHeight: '34px', gap: 0.75 }
      : null;

  const iconDensitySx = isSmallDevice
    ? { width: 16, height: 16, '& svg': { fontSize: '13px' } }
    : isMediumDevice
      ? { width: 18, height: 18, '& svg': { fontSize: '14px' } }
      : null;

  const cardLabelFontSize = isSmallDevice ? '0.72rem' : isMediumDevice ? '0.8rem' : '0.875rem';
  const controlLabelFontSize = isSmallDevice ? '0.72rem' : isMediumDevice ? '0.78rem' : '0.875rem';

  const compactVisibleCount = isSmallDevice ? 2 : isMediumDevice ? 3 : cards.length;
  const compactCards = isCompactDevice && !showAllSmallCards;
  const displayedCards = compactCards ? cards.slice(0, compactVisibleCount) : cards;
  const hiddenCount = compactCards ? Math.max(cards.length - displayedCards.length, 0) : 0;

  return (
    <Box 
      width="100%" 
      display="flex" 
      flexDirection="row" 
      alignItems="center"
      justifyContent="center"
      gap={isCompactDevice ? 0.75 : 1.5}
      px={isCompactDevice ? 1 : 2}
      pt={isCompactDevice ? 1 : 1.5}
      pb={isCompactDevice ? 0.75 : 1}
      flexWrap="wrap"
    >
      {displayedCards.map((card, index) => (
        <Tooltip key={index} title={card.description} arrow placement="top">
          <CardPaper
            elevation={1}
            onClick={() => setMessage(card.prompt)}
            sx={{
              flex: '0 1 auto',
              ...(cardDensitySx || {}),
            }}
          >
            <IconWrapper sx={iconDensitySx || {}}>
              {card.icon}
            </IconWrapper>
            <Typography 
              variant="body2" 
              fontWeight={500} 
              color="text.secondary"
              sx={{ fontSize: cardLabelFontSize, whiteSpace: 'nowrap', lineHeight: 1.1 }}
            >
              {card.shortTitle}
            </Typography>
          </CardPaper>
        </Tooltip>
      ))}

      {isCompactDevice && hiddenCount > 0 && (
        <Tooltip title={`Show ${hiddenCount} more prompts`} arrow placement="top">
          <CardPaper
            elevation={1}
            onClick={() => setShowAllSmallCards(true)}
            sx={{
              ...(cardDensitySx || {}),
              borderStyle: 'dashed',
            }}
          >
            <Typography variant="body2" fontWeight={600} color="primary.main" sx={{ fontSize: controlLabelFontSize, lineHeight: 1.1 }}>
              More (+{hiddenCount})
            </Typography>
          </CardPaper>
        </Tooltip>
      )}

      {isCompactDevice && showAllSmallCards && cards.length > compactVisibleCount && (
        <Tooltip title="Collapse prompt list" arrow placement="top">
          <CardPaper
            elevation={1}
            onClick={() => setShowAllSmallCards(false)}
            sx={{
              ...(cardDensitySx || {}),
              borderStyle: 'dashed',
            }}
          >
            <Typography variant="body2" fontWeight={600} color="primary.main" sx={{ fontSize: controlLabelFontSize, lineHeight: 1.1 }}>
              Less
            </Typography>
          </CardPaper>
        </Tooltip>
      )}
    </Box>
  );
};

export default MainCards;